import { Subject } from 'rxjs';
import Cookies from 'js-cookie';
import {
    MqttWorkerConnectOptions,
    MqttWorkerWireMessage,
} from './mqtt-worker-types';

type PersistentGatewayMode = 'startMission' | 'startSim';
type GatewayMode =
    | PersistentGatewayMode
    | 'stopMission'
    | 'stopSim'
    | 'waitCmdStart'
    | 'sendCmdStart'
    | 'sendTelemetry'
    | 'subscribeTopic'
    | 'unsubscribeTopic'
    | 'publishTopic'
    | 'reauth';

type PendingGatewayRequest = {
    mode: GatewayMode;
    resolve: () => void;
    reject: (error: Error) => void;
    timeout: ReturnType<typeof setTimeout>;
    sentAt: number;
    attempt: number;
};

const GATEWAY_REQUEST_TIMEOUT_MS = 5000;
const GATEWAY_SOCKET_OPEN_TIMEOUT_MS = 10000;
/**
 * #576: what is refused once the session has ended (evicted, expired, renewal refused).
 * Nothing reopens it; signing in again loads the page anew.
 */
const SESSION_ENDED_MESSAGE = 'The sign-in session has ended: sign in again';
/**
 * digitaltwins-service#576 (B3): reconnect with exponential backoff and jitter (a
 * random delay up to 1 s doubling to a 30 s cap, never under 250 ms) instead of a
 * fixed 1 s, so a gateway restart or a persistent rejection does not turn every open
 * tab into a 1 Hz loop. The backoff starts over only once a connection has stayed up
 * GATEWAY_STABLE_RESET_MS: a socket that opens then drops at once keeps backing off.
 */
const GATEWAY_RECONNECT_BASE_MS = 1000;
const GATEWAY_RECONNECT_MAX_MS = 30000;
const GATEWAY_RECONNECT_MIN_MS = 250;
const GATEWAY_STABLE_RESET_MS = 30000;
/**
 * TWIN-CODE-REVIEW 2026-10-24: keepalive interval. The ping only keeps idle proxies from closing
 * the socket (edge and QA ingress read timeouts are 60 s; nothing reads the pong), so 1 s was
 * ~20x more often than needed and cost the twin ~1.2 millicores per open tab. 20 s keeps three
 * pings inside the 60 s timeout.
 */
export const GATEWAY_PING_INTERVAL_MS = 20000;
/** #576: close codes the gateway uses for session enforcement (dtt-ws-adapter). */
const WS_CLOSE_SESSION_TERMINATED = 4401;
const WS_CLOSE_TOKEN_EXPIRED = 4402;
/** #576 (B4): renew the access token this long before it expires. */
const TOKEN_REFRESH_LEAD_MS = 5 * 60 * 1000;
/** #576 (B4): never call /auth/refresh more often than this, unless a 4402 forces it. */
const TOKEN_REFRESH_MIN_INTERVAL_MS = 60000;
/**
 * #576 (B4): what POST /auth/refresh answered. 'rejected' only for its 401
 * refresh_rejected; any other status, a non-JSON answer, a redirect or no answer at
 * all is 'unavailable' (retried, never a sign-out). 'skipped': not called (throttled).
 */
type RefreshOutcome = 'ok' | 'rejected' | 'unavailable' | 'skipped';
/**
 * #508: how often to compare the stored token with the one the socket holds.
 * The IDE refreshes `kc_token` long before the 7200 s access-token lifespan
 * runs out, so a minute keeps the gateway's copy fresh with a wide margin.
 */
const GATEWAY_REAUTH_CHECK_MS = 60000;

/**
 * Drop-in alternative to `mqttWorkerClient` that routes ALL MQTT traffic through
 * the WebSocket gateway instead of connecting to the broker directly.
 *
 * It mirrors the subset of the `mqttWorkerClient` interface that `mqtt-service.ts`
 * relies on (connect/subscribe/unsubscribe/publish/disconnectGlobal/reconnect +
 * the onMessage/onConnected/onConnectionLost/onIdleTimeout subjects), so it can be
 * swapped in via a feature flag while the direct-connection path is kept intact
 * for comparison.
 *
 * VirtualLeanbot uses semantic APIs without user/topic; the backend resolves
 * user from the WebSocket token. Generic MQTT methods remain for legacy pages.
 * Push messages include channel/mission metadata and a compatibility topic.
 */
class MqttGatewayClient {
    onMessage = new Subject<MqttWorkerWireMessage>();
    onConnected = new Subject<void>();
    onConnectionLost = new Subject<string>();
    onIdleTimeout = new Subject<void>();
    /** #576 (B3): the gateway ended this session (signed in elsewhere / session revoked). */
    onSessionTerminated = new Subject<string>();
    onLog = new Subject<string>();
    /** Handed to WebSocket, not confirmation of gateway receipt or MQTT delivery. */
    onGatewaySend = new Subject<string>();
    /** Lifecycle/ACK diagnostics, without telemetry payloads or socket URLs. */
    onDiagnostic = new Subject<string>();

    private ws: WebSocket | null = null;
    private connected = false;
    private closedByUser = false;
    /** #576: set once the gateway terminated the session; no reconnect until connect() is called again. */
    private sessionEnded = false;
    private reconnectAttempt = 0;
    /** #576: started when a connection is ready; resets the backoff if it stays up. */
    private stableTimer: any = null;
    /** #576 (B4): the gateway said `token_expired`; its close is handled as a 4402. */
    private tokenExpiredPending = false;
    /** #576 (B4): one /auth/refresh at a time; concurrent callers share its outcome. */
    private refreshInFlight: Promise<RefreshOutcome> | null = null;
    private lastHttpRefreshAt = 0;
    // Scoped to this iframe document. Only the parent's explicit request grants permission.
    private readonly onDemand = (() => {
        try {
            return typeof window !== 'undefined' && window.parent !== window
                && new URL(window.location.href).searchParams.get('mqtt') === 'on-demand';
        } catch { return false; }
    })();
    private connectionPermission = false;
    /**
     * #576: the current grant has initialized a connection. Until it has, a failed
     * initialization gives the permission back; after that, a failed reconnect keeps it and
     * the backoff carries on. Cleared whenever the grant or the session ends, so a later
     * grant never inherits an earlier success.
     */
    private permissionInitialized = false;
    /** Topics this client wants subscribed (re-sent on reconnect). */
    private desiredTopics = new Set<string>();
    /** Free-form raw topics subscribed through the raw-topic API (re-sent on reconnect). */
    private desiredRawTopics = new Set<string>();
    /** Persistent VirtualLeanbot subscriptions re-sent on reconnect. */
    private desiredModes = new Set<PersistentGatewayMode>();
    /** One-shot command wait remains desired until cmdStart arrives. */
    private waitingForCmdStart = false;
    /** Messages buffered while the socket is not open yet. */
    private pending: string[] = [];
    private connectionPromise: Promise<void> | null = null;
    private pendingRequests = new Map<string, PendingGatewayRequest>();
    private requestSequence = 0;
    private socketSequence = 0;
    private uncorrelatedReplies = 0;
    private pingTimer: any = null;
    private reconnectTimer: any = null;
    /** #508: token the gateway currently holds for this socket. */
    private socketToken = '';
    /** #508: last token offered via `reauth`, so a rejected one is not resent every tick. */
    private reauthAttemptedToken = '';
    private reauthInFlight = false;
    private reauthTimer: any = null;

    get isConnected() {
        return this.connected;
    }

    get isConnectionOnDemand() { return this.onDemand; }
    get connectionAllowed() { return !this.onDemand || this.connectionPermission; }

    /** Called only for an origin/source-checked parent request, never by internal reconnect/UI. */
    async requestConnection(): Promise<void> {
        // #576: once the session has ended, no grant is given.
        if (this.sessionEnded) throw new Error(SESSION_ENDED_MESSAGE);
        this.connectionPermission = true;
        return this.connect();
    }

    private denyConnection() {
        if (!this.onDemand) return;
        this.connectionPermission = false;
        this.permissionInitialized = false;
        this.connected = false;
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
    }

    // The gateway builds the topic from (user, channel[, mission]); we only need
    // to decompose the full topic the caller already assembled.
    private parseTopic(topic: string) {
        const parts = (topic || '').split('/');
        // leanbot/{user}/{channel}[/{mission|+}]
        return {
            user: parts[1] || '',
            channel: parts[2] || '',
            mission: parts[3] && parts[3] !== '+' ? parts[3] : undefined,
        };
    }

    private authToken(): { value: string; source: string } {
        try {
            const cookieToken =
                typeof document !== 'undefined'
                    ? Cookies.get('token') || ''
                    : '';
            const sessionToken =
                typeof sessionStorage !== 'undefined'
                    ? sessionStorage.getItem('token')
                    : '';
            const localToken =
                typeof localStorage !== 'undefined'
                    ? localStorage.getItem('kc_token') || localStorage.getItem('token')
                    : '';
            return {
                value: cookieToken || sessionToken || localToken || '',
                source: cookieToken ? 'cookie' : sessionToken ? 'sessionStorage' : localToken ? 'localStorage' : 'none',
            };
        } catch {
            return { value: '', source: 'unavailable' };
        }
    }

    private gatewayUrl(): { url: string; hasToken: boolean; tokenSource: string } {
        const configured = (conf as any)?.websocket?.url;
        if (!configured) return { url: '', hasToken: false, tokenSource: 'none' };
        try {
            const url =
                typeof window === 'undefined'
                    ? new URL(configured)
                    : new URL(configured, window.location.href);
            const token = this.authToken();
            if (token.value) url.searchParams.set('token', token.value);
            this.socketToken = token.value;
            this.reauthAttemptedToken = token.value;
            const hasToken = Boolean(url.searchParams.get('token'));
            return {
                url: url.toString(), hasToken,
                tokenSource: token.value ? token.source : hasToken ? 'configuredUrl' : token.source,
            };
        } catch {
            return { url: configured, hasToken: false, tokenSource: 'unavailable' };
        }
    }

    async connect(_options?: MqttWorkerConnectOptions): Promise<void> {
        void _options;
        // #576: an ended session stays ended. No call reopens it (sendTelemetry, waitCmdStart,
        // a RUN, ...), whatever token the page holds now; signing in again reloads the page.
        if (this.sessionEnded) throw new Error(SESSION_ENDED_MESSAGE);
        if (!this.connectionAllowed) throw new Error('Digital Twin MQTT requires a request from the parent');
        this.closedByUser = false;
        this.tokenExpiredPending = false;
        // OPEN is not enough: all initialization ACKs must settle first.
        if (this.connectionPromise) return this.connectionPromise;
        if (this.ws?.readyState === WebSocket.OPEN && this.connected) return;
        return this.openSocket();
    }

    private diagnostic(event: string, details: Record<string, unknown> = {}) {
        // A diagnostic sink must not change connection, ACK or retry behavior.
        try {
            this.onDiagnostic.next(`${event} ${JSON.stringify({
                attempt: this.socketSequence,
                ...details,
                hidden: typeof document !== 'undefined' ? document.hidden : null,
            })}`);
        } catch { /* best-effort logging */ }
    }

    private openSocket(): Promise<void> {
        if (!this.connectionAllowed) return Promise.reject(new Error('Digital Twin MQTT requires a request from the parent'));
        if (this.connectionPromise) return this.connectionPromise;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        const connection = this.gatewayUrl();
        const url = connection.url;
        if (!url) {
            this.denyConnection();
            const error = new Error('Missing WebSocket gateway URL');
            console.error(`MqttGatewayClient: ${error.message}`);
            return Promise.reject(error);
        }

        const attempt = ++this.socketSequence;
        const startedAt = Date.now();
        let initialized = false;
        this.uncorrelatedReplies = 0;
        this.diagnostic('socket-create', {
            modes: [...this.desiredModes], openTimeoutMs: GATEWAY_SOCKET_OPEN_TIMEOUT_MS,
            hasToken: connection.hasToken, tokenSource: connection.tokenSource,
        });
        let ws: WebSocket;
        try { ws = new WebSocket(url); }
        catch (error) { this.denyConnection(); return Promise.reject(error); }
        this.ws = ws;

        let resolveConnection!: () => void;
        let rejectConnection!: (error: Error) => void;
        let settled = false;
        const connectionPromise = new Promise<void>((resolve, reject) => {
            resolveConnection = resolve;
            rejectConnection = reject;
        });
        this.connectionPromise = connectionPromise;

        const socketOpenTimeout = setTimeout(() => {
            const error = new Error('WebSocket gateway connection timeout');
            settle(error);
            try {
                ws.close();
            } catch {
                /* ignore */
            }
        }, GATEWAY_SOCKET_OPEN_TIMEOUT_MS);

        const settle = (error?: Error) => {
            if (settled) return;
            settled = true;
            // #576: until the current grant has initialized a connection, a failed
            // initialization requires a new parent request; revoke before close/onclose can
            // schedule another socket. After that, a failed reconnect (the gateway restarting
            // or briefly unreachable) keeps the grant and the backoff carries on.
            if (error && !this.permissionInitialized) this.denyConnection();
            clearTimeout(socketOpenTimeout);
            if (this.connectionPromise === connectionPromise) {
                this.connectionPromise = null;
            }
            this.diagnostic(error ? 'connection-failed' : 'connection-ready', {
                attempt, elapsedMs: Date.now() - startedAt, error: error?.message,
            });
            if (error) rejectConnection(error);
            else resolveConnection();
        };

        ws.onopen = async () => {
            if (this.ws !== ws) return;
            clearTimeout(socketOpenTimeout);
            this.connected = true;
            this.diagnostic('socket-open', { attempt, elapsedMs: Date.now() - startedAt });

            try {
                // NestJS must acknowledge semantic subscriptions before the iframe
                // announces readiness or queued telemetry is flushed.
                for (const mode of this.desiredModes) {
                    await this.sendRequest(mode);
                }
                if (this.waitingForCmdStart) {
                    await this.sendRequest('waitCmdStart');
                }
                this.desiredTopics.forEach(topic => this.sendSubscribe(topic));
                for (const topic of this.desiredRawTopics) {
                    await this.sendRequest('subscribeTopic', { topic });
                }
                // #576: the session may have ended, or this socket been given up, while the ACKs
                // were awaited: then nothing is flushed, no timer starts, nothing is announced.
                if (!this.stillWanted(attempt)) {
                    throw new Error(this.sessionEnded ? SESSION_ENDED_MESSAGE : 'WebSocket gateway connection no longer wanted');
                }

                const buffered = this.pending.splice(0);
                buffered.forEach(message => this.rawSend(message));
                this.setupPing();
                this.setupReauth();
                initialized = true;
                // #576: the grant this socket opened under has initialized, unless it or the
                // session ended meanwhile (an ACK that arrives after that does not count).
                if (this.onDemand && this.stillWanted(attempt)) this.permissionInitialized = true;
                this.onConnected.next();
                settle();
                this.armStableReset(ws);
            } catch (error) {
                const gatewayError = error instanceof Error ? error : new Error(String(error));
                this.onLog.next(`Gateway initialization failed: ${gatewayError.message}`);
                settle(gatewayError);
                try {
                    ws.close();
                } catch {
                    /* ignore */
                }
            }
        };

        ws.onmessage = event => this.handleMessage(event.data);

        ws.onerror = () => {
            this.diagnostic('socket-error', { attempt, elapsedMs: Date.now() - startedAt });
            // onclose will follow and handle reconnect.
        };

        ws.onclose = event => {
            if (this.ws !== ws) return;
            this.diagnostic('socket-close', {
                attempt, elapsedMs: Date.now() - startedAt, initialized,
                code: event?.code ?? null, reason: event?.reason || '', wasClean: event?.wasClean ?? null,
                closedByUser: this.closedByUser,
                pendingModes: [...this.pendingRequests.values()].map(request => request.mode),
            });
            this.ws = null;
            this.connected = false;
            this.teardownPing();
            this.teardownReauth();
            this.clearStableReset();
            const error = new Error('WebSocket gateway closed');
            this.rejectPendingRequests(error);
            settle(error);
            // #576: the gateway may announce why (sessionTerminated) before it closes;
            // the message and the close are handled here once, by one branch.
            const tokenExpired = event?.code === WS_CLOSE_TOKEN_EXPIRED || this.tokenExpiredPending;
            this.tokenExpiredPending = false;
            // Already ended by the announcement: nothing more to do.
            if (this.sessionEnded) return;
            // #576 (B3): 4401 = the session is over (another sign-in under #395, an idle
            // timeout, a sign-out elsewhere: the gateway cannot tell which). Reconnecting
            // with the same session cannot succeed.
            if (event?.code === WS_CLOSE_SESSION_TERMINATED) {
                this.endSession('session_terminated');
                return;
            }
            this.onConnectionLost.next(error.message);
            // #576 (B4): the access token expired; a socket opened with it would be
            // refused, so renew it first and reconnect only with the new one.
            if (tokenExpired) {
                void this.recoverExpiredToken();
                return;
            }
            this.scheduleReconnect();
        };

        return connectionPromise;
    }

    /** #576 (B3): exponential backoff with jitter, capped, floor GATEWAY_RECONNECT_MIN_MS. */
    private nextReconnectDelay(): number {
        const ceiling = Math.min(GATEWAY_RECONNECT_MAX_MS, GATEWAY_RECONNECT_BASE_MS * 2 ** this.reconnectAttempt);
        this.reconnectAttempt = Math.min(this.reconnectAttempt + 1, 16);
        return Math.max(GATEWAY_RECONNECT_MIN_MS, Math.round(Math.random() * ceiling));
    }

    /** #576: reset the backoff once the connection `ws` has stayed up long enough. */
    private armStableReset(ws: WebSocket) {
        this.clearStableReset();
        this.stableTimer = setTimeout(() => {
            this.stableTimer = null;
            if (this.ws === ws && this.connected) this.reconnectAttempt = 0;
        }, GATEWAY_STABLE_RESET_MS);
    }

    private clearStableReset() {
        if (this.stableTimer) clearTimeout(this.stableTimer);
        this.stableTimer = null;
    }

    /** True while nothing has ended, closed or replaced the connection seen at `attempt`. */
    private isCurrent(attempt: number): boolean {
        return attempt === this.socketSequence && !this.sessionEnded && !this.closedByUser;
    }

    /** isCurrent, and this client may still open a socket by itself. */
    private stillWanted(attempt: number): boolean {
        return this.isCurrent(attempt) && this.connectionAllowed;
    }

    /**
     * #576 (B4): after the access token expired. Renew it, then reconnect with the new
     * one; when Keycloak gives no verdict, retry the renewal with the backoff (never a
     * socket with the expired token); when it rejects the refresh token, the session
     * is over. A result that arrives after the client was closed, ended or reconnected
     * meanwhile is dropped.
     */
    private async recoverExpiredToken(): Promise<void> {
        const attempt = this.socketSequence;
        const outcome = await this.refreshTokenViaHttp(true);
        // #576: a rejection ends the session even when this client may not reconnect by
        // itself (an on-demand embed whose grant failed its first initialization has given
        // its permission back). The sign-in is over either way; before, nothing told the user so.
        if (outcome === 'rejected') {
            if (this.isCurrent(attempt)) this.endSession('refresh_rejected');
            return;
        }
        if (!this.stillWanted(attempt)) return;
        if (outcome === 'ok') {
            this.scheduleReconnect();
        } else {
            this.scheduleRefreshRetry(attempt);
        }
    }

    /** #576 (B4): the next renewal attempt, in the reconnect timer slot (one timer at a time). */
    private scheduleRefreshRetry(attempt: number) {
        if (!this.stillWanted(attempt) || this.reconnectTimer) return;
        const delayMs = this.nextReconnectDelay();
        this.diagnostic('refresh-retry-scheduled', { delayMs, attempt: this.reconnectAttempt });
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            if (!this.stillWanted(attempt)) return;
            void this.recoverExpiredToken();
        }, delayMs);
    }

    private scheduleReconnect() {
        if (!this.connectionAllowed || this.closedByUser || this.sessionEnded || this.reconnectTimer) return;
        const delayMs = this.nextReconnectDelay();
        this.diagnostic('reconnect-scheduled', { delayMs, attempt: this.reconnectAttempt });
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            if (!this.connectionAllowed || this.closedByUser || this.sessionEnded) return;
            this.openSocket().catch(error => {
                const message = error instanceof Error ? error.message : String(error);
                this.onLog.next(`Gateway reconnect failed: ${message}`);
            });
        }, delayMs);
    }

    /**
     * #576 (B3): the session is over: 'session_terminated' (the gateway ended it, for a
     * reason it does not know) or 'refresh_rejected' (the sign-in itself expired).
     * Stop all reconnects, tell the page, and show the matching message with a way to
     * sign in again.
     */
    private endSession(reason: string) {
        const first = !this.sessionEnded;
        this.sessionEnded = true;
        // #576: the grant's earlier success no longer counts.
        this.permissionInitialized = false;
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
        this.clearStableReset();
        this.teardownPing();
        this.teardownReauth();
        this.connected = false;
        // #576: nothing more goes up or comes down on this session. Requests still waiting fail
        // now, not when the close handshake ends; nothing queued is kept; the socket is closed if
        // still open (a renewal refused before expiry leaves it open). `this.ws` stays set so its
        // onclose runs, and that reconnects nothing once the session has ended.
        this.pending = [];
        this.rejectPendingRequests(new Error(SESSION_ENDED_MESSAGE));
        try {
            this.ws?.close(1000, 'session ended');
        } catch {
            /* already closing */
        }
        if (!first) return;
        const expired = reason === 'refresh_rejected';
        this.diagnostic('session-terminated', { reason });
        this.onSessionTerminated.next(reason);
        this.onConnectionLost.next(expired
            ? 'Session expired'
            : 'Session ended');
        showSessionEndedNotice(expired ? 'expired' : 'ended');
    }

    /** #576 (B4): exp (ms) of a JWT access token, or 0 when it cannot be read. */
    private tokenExpiryMs(token: string): number {
        try {
            const part = (token.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/');
            const json = JSON.parse(atob(part.padEnd(Math.ceil(part.length / 4) * 4, '=')));
            return typeof json.exp === 'number' ? json.exp * 1000 : 0;
        } catch {
            return 0;
        }
    }

    /**
     * #576 (B4): renew the `token` cookie through POST /auth/refresh (the server renews
     * it from the httpOnly `rf` refresh cookie). Nothing else on the IDE page renews
     * it, so without this the #551 in-socket reauth has nothing new to hand over.
     * Only the endpoint's JSON contract is trusted: 200 {ok: true} renewed, 401
     * {code: 'refresh_rejected'} over; a redirect is not followed, and anything else
     * is 'unavailable'. Callers decide what an outcome means; this ends nothing.
     */
    private refreshTokenViaHttp(force = false): Promise<RefreshOutcome> {
        if (this.refreshInFlight) return this.refreshInFlight;
        if (typeof fetch === 'undefined') return Promise.resolve('unavailable');
        if (!force && Date.now() - this.lastHttpRefreshAt < TOKEN_REFRESH_MIN_INTERVAL_MS) {
            return Promise.resolve('skipped');
        }
        this.lastHttpRefreshAt = Date.now();
        const request = (async (): Promise<RefreshOutcome> => {
            try {
                const res = await fetch('/auth/refresh', {
                    method: 'POST',
                    credentials: 'same-origin',
                    cache: 'no-store',
                    redirect: 'manual',
                    headers: { Accept: 'application/json' },
                });
                const body: any = await res.json().catch(() => null);
                if (res.status === 200 && body?.ok === true) return 'ok';
                if (res.status === 401 && body?.code === 'refresh_rejected') return 'rejected';
                this.diagnostic('token-refresh-unavailable', { status: res.status, code: body?.code ?? null });
                return 'unavailable';
            } catch {
                return 'unavailable';
            } finally {
                this.refreshInFlight = null;
            }
        })();
        this.refreshInFlight = request;
        return request;
    }

    private setupPing() {
        this.teardownPing();
        this.pingTimer = setInterval(() => {
            this.rawSend(JSON.stringify({ mode: 'ping', data: 'ping' }));
        }, GATEWAY_PING_INTERVAL_MS);
    }

    private teardownPing() {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
    }

    // #508: the gateway validates the token it captured at connect on every
    // revalidation tick, and Keycloak rejects it once it passes `exp` (7200 s).
    // Hand the gateway the refreshed token instead of letting it close the socket.
    private setupReauth() {
        this.teardownReauth();
        this.reauthTimer = setInterval(() => {
            void (async () => {
                // #576 (B4): renew the cookie token shortly before it expires, then
                // hand the new token to the socket (#551 reauth).
                const attempt = this.socketSequence;
                const exp = this.tokenExpiryMs(this.authToken().value);
                if (exp && exp - Date.now() < TOKEN_REFRESH_LEAD_MS) {
                    const outcome = await this.refreshTokenViaHttp();
                    if (!this.stillWanted(attempt)) return;
                    if (outcome === 'rejected') {
                        this.endSession('refresh_rejected');
                        return;
                    }
                    // 'unavailable': the socket keeps its token; the next tick tries again.
                }
                await this.refreshGatewayAuth();
            })();
        }, GATEWAY_REAUTH_CHECK_MS);
    }

    private teardownReauth() {
        if (this.reauthTimer) {
            clearInterval(this.reauthTimer);
            this.reauthTimer = null;
        }
    }

    private async refreshGatewayAuth(): Promise<void> {
        if (!this.connected || this.reauthInFlight) return;
        const token = this.authToken().value;
        if (!token || token === this.socketToken || token === this.reauthAttemptedToken) return;
        this.reauthInFlight = true;
        this.reauthAttemptedToken = token;
        try {
            await this.sendRequest('reauth', { token });
            this.socketToken = token;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.onLog.next(`Gateway reauth failed: ${message}`);
        } finally {
            this.reauthInFlight = false;
        }
    }

    private handleMessage(raw: any) {
        // #576: nothing that still arrives after the session ended is acted on: no command, no ACK.
        if (this.sessionEnded) return;
        let msg: any;
        try {
            msg = JSON.parse(raw);
        } catch {
            if (this.pendingRequests.size && this.uncorrelatedReplies++ < 3) {
                this.diagnostic('reply-invalid-json', { bytes: typeof raw === 'string' ? raw.length : null });
            }
            return;
        }

        // #576: the gateway announces why before it closes. token_expired is not the
        // end of the session: its close (4402) renews the token. Anything else is.
        if (msg && msg.mode === 'sessionTerminated') {
            if (msg.reason === 'token_expired') this.tokenExpiredPending = true;
            else this.endSession('session_terminated');
            return;
        }

        if (msg && typeof msg === 'object' && msg.reqId) {
            const pendingRequest = this.pendingRequests.get(msg.reqId);
            if (pendingRequest) {
                clearTimeout(pendingRequest.timeout);
                this.pendingRequests.delete(msg.reqId);
                if (pendingRequest.mode !== 'sendTelemetry' || msg.ok === false) {
                    this.diagnostic(msg.ok === false ? 'ack-rejected' : 'ack-received', {
                        attempt: pendingRequest.attempt, mode: pendingRequest.mode, reqId: msg.reqId,
                        elapsedMs: Date.now() - pendingRequest.sentAt, ok: msg.ok ?? null,
                        error: msg.ok === false ? msg.error : undefined,
                    });
                }
                if (msg.ok === false) {
                    pendingRequest.reject(
                        new Error(msg.error || `${pendingRequest.mode} was rejected by the gateway`),
                    );
                } else {
                    pendingRequest.resolve();
                }
            } else if (this.uncorrelatedReplies++ < 3) {
                this.diagnostic('ack-unmatched', { reqId: msg.reqId, mode: msg.mode });
            }
        } else if (msg && this.pendingRequests.size && msg.mode !== 'mqttMessage' && msg.mode !== 'pong'
            && this.uncorrelatedReplies++ < 3) {
            // Older gateway replies may omit reqId. Show only the shape, never the payload.
            this.diagnostic('reply-without-request-id', {
                mode: msg.mode, keys: Object.keys(msg),
                pendingModes: [...this.pendingRequests.values()].map(request => request.mode),
            });
        }

        if (msg && msg.mode === 'mqttMessage') {
            if (msg.channel === 'cmdStart') {
                this.waitingForCmdStart = false;
            }
            this.onMessage.next({
                destinationName: msg.topic || '',
                payloadString: msg.data,
                channel: msg.channel,
                user: msg.user,
                mission: msg.mission,
            });
        }
    }

    private rejectPendingRequests(error: Error) {
        this.pendingRequests.forEach(request => {
            clearTimeout(request.timeout);
            request.reject(error);
        });
        this.pendingRequests.clear();
    }

    private reportGatewaySend(data: string) {
        // Diagnostics must never reject a sent request or interrupt pending flushes.
        // Read only the serialized message, never the socket URL/authentication.
        try {
            const { mode, reqId: _reqId, ...payload } = JSON.parse(data);
            if (mode === 'ping') return;
            // #508: never echo a credential into the visible send log.
            if ('token' in payload) payload.token = '[redacted]';
            const fields = Object.entries(payload)
                .map(([key, value]) => `${key}=${JSON.stringify(value)}`);
            this.onGatewaySend.next(`${mode}(${fields.join(', ')})`);
        } catch {
            // Logging is best-effort; send/ACK semantics remain unchanged.
        }
    }

    private rawSend(data: string) {
        // #576: once the session has ended nothing is sent, and nothing is kept to send later.
        if (this.sessionEnded) return false;
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(data);
            this.reportGatewaySend(data);
            return true;
        }
        this.pending.push(data);
        return false;
    }

    private sendRequest(
        mode: GatewayMode,
        payload: Record<string, unknown> = {},
    ): Promise<void> {
        const ws = this.ws;
        if (!this.connected || !ws || ws.readyState !== WebSocket.OPEN) {
            return Promise.reject(new Error(`WebSocket gateway is not ready for ${mode}`));
        }

        const reqId = `gateway-${Date.now()}-${++this.requestSequence}`;
        const sentAt = Date.now();
        const attempt = this.socketSequence;
        return new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(reqId);
                this.diagnostic('ack-timeout', {
                    attempt, mode, reqId, elapsedMs: Date.now() - sentAt,
                    timeoutMs: GATEWAY_REQUEST_TIMEOUT_MS, socketState: ws.readyState,
                });
                reject(new Error(`${mode} gateway acknowledgement timeout`));
            }, GATEWAY_REQUEST_TIMEOUT_MS);

            this.pendingRequests.set(reqId, { mode, resolve, reject, timeout, sentAt, attempt });
            let data: string;
            try {
                data = JSON.stringify({ mode, reqId, ...payload });
                ws.send(data);
            } catch (error) {
                clearTimeout(timeout);
                this.pendingRequests.delete(reqId);
                this.diagnostic('request-send-failed', {
                    attempt, mode, reqId, error: error instanceof Error ? error.message : String(error),
                });
                reject(error instanceof Error ? error : new Error(String(error)));
                return;
            }
            this.reportGatewaySend(data);
            if (mode !== 'sendTelemetry') {
                this.diagnostic('request-sent', { attempt, mode, reqId, timeoutMs: GATEWAY_REQUEST_TIMEOUT_MS });
            }
        });
    }

    private async setPersistentMode(
        mode: PersistentGatewayMode,
        enabled: boolean,
    ): Promise<void> {
        if (enabled) {
            if (this.desiredModes.has(mode)) return;
            this.desiredModes.add(mode);
            this.diagnostic('subscription-desired', { mode, connected: this.connected });
            if (this.connected) {
                await this.sendRequest(mode);
            }
            return;
        }

        if (!this.desiredModes.delete(mode) || !this.connected) return;
        const stopMode = mode === 'startMission' ? 'stopMission' : 'stopSim';
        await this.sendRequest(stopMode);
    }

    async sendTelemetry(mission: string, data: string): Promise<void> {
        await this.connect();
        const request = this.sendRequest('sendTelemetry', { mission, data });
        if (/^[0-9a-f]{2}A$/i.test(data)) {
            await request;
            return;
        }

        void request.catch(error => {
            const message = error instanceof Error ? error.message : String(error);
            this.onLog.next(`sendTelemetry failed: ${message}`);
        });
    }

    /**
     * #576: one frame of a physical Leanbot, relayed by the /gateway page; the gateway
     * publishes it on the device topic of the authenticated user (MQTT_GATEWAYTOPIC). Fire
     * and forget, like the frames: handed over only on an open socket of a live session and
     * dropped otherwise. Never buffered and never a reason to connect, so a device that keeps
     * sending after the session ended or the socket dropped sends nothing and piles up nothing.
     * @returns whether the frame was handed to the WebSocket.
     */
    sendDeviceData(data: string): boolean {
        const ws = this.ws;
        if (!this.connected || this.sessionEnded || !ws || ws.readyState !== WebSocket.OPEN) return false;
        try {
            ws.send(JSON.stringify({ mode: 'dataFromLeanbotToMQTT', data }));
            return true;
        } catch {
            return false;
        }
    }

    async startMission(): Promise<void> {
        await this.setPersistentMode('startMission', true);
    }

    async stopMission(): Promise<void> {
        await this.setPersistentMode('startMission', false);
    }

    async startSim(): Promise<void> {
        await this.setPersistentMode('startSim', true);
    }

    async stopSim(): Promise<void> {
        await this.setPersistentMode('startSim', false);
    }

    async waitCmdStart(): Promise<void> {
        await this.connect();
        // Every mission run explicitly re-arms the one-shot backend waiter. A
        // previous START timeout may leave this desired-state flag set even
        // though the old WS subscription was removed or the socket reconnected.
        this.waitingForCmdStart = true;
        try {
            await this.sendRequest('waitCmdStart');
        } catch (error) {
            this.waitingForCmdStart = false;
            throw error;
        }
    }

    async sendCmdStart(): Promise<void> {
        await this.connect();
        // `data` is ignored by the current gateway (it always publishes the
        // exact start-bot command itself) but pre-migration gateways publish
        // `data.data ?? '{"action":"start"}'`, which the waitCmdStart handler
        // rejects. Sending it keeps client/backend deploys order-independent.
        await this.sendRequest('sendCmdStart', { data: 'start-bot' });
    }

    private sendSubscribe(topic: string) {
        const { user, channel } = this.parseTopic(topic);
        this.rawSend(JSON.stringify({ mode: 'mqttSubscribe', user, channel }));
    }

    async subscribe(topic: string) {
        if (this.desiredTopics.has(topic)) return;
        this.desiredTopics.add(topic);
        if (this.connected) this.sendSubscribe(topic);
    }

    async unsubscribe(topic: string) {
        if (!this.desiredTopics.delete(topic) || !this.connected) return;
        const { user, channel } = this.parseTopic(topic);
        this.rawSend(JSON.stringify({ mode: 'mqttUnsubscribe', user, channel }));
    }

    async publish(topic: string, payload: string) {
        const { user, channel, mission } = this.parseTopic(topic);
        this.rawSend(
            JSON.stringify({ mode: 'mqttPublish', user, channel, mission, data: payload }),
        );
    }

    // ──────────────────────────────────────────────────────────────────────
    // Raw-topic API. The topic is sent verbatim and used verbatim — it is NOT
    // confined to `leanbot/{user}/`, which is the whole point: it exists to
    // reach a physical gateway tree or any other stream on the broker.
    //
    // Kept apart from subscribe/publish above, which still speak the fixed
    // `leanbot/{user}/{channel}` scheme and must keep their isolation.
    //
    // Incoming messages arrive on `onMessage` with channel `rawTopic` and the
    // concrete topic in `destinationName`.
    // ──────────────────────────────────────────────────────────────────────

    async subscribeTopic(topic: string): Promise<void> {
        await this.connect();
        if (this.desiredRawTopics.has(topic)) return;
        // Remember the intent only after the gateway accepts it, otherwise a
        // rejected topic would be replayed on every reconnect.
        await this.sendRequest('subscribeTopic', { topic });
        this.desiredRawTopics.add(topic);
    }

    async unsubscribeTopic(topic: string): Promise<void> {
        if (!this.desiredRawTopics.delete(topic) || !this.connected) return;
        await this.sendRequest('unsubscribeTopic', { topic });
    }

    async publishTopic(topic: string, payload: string): Promise<void> {
        await this.connect();
        await this.sendRequest('publishTopic', { topic, data: payload });
    }

    async release() {
        // Per-tab no-op; the gateway cleans up on socket close.
    }

    async disconnectGlobal() {
        this.denyConnection();
        this.closedByUser = true;
        this.teardownPing();
        this.teardownReauth();
        this.clearStableReset();
        this.tokenExpiredPending = false;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.desiredTopics.clear();
        this.desiredRawTopics.clear();
        this.desiredModes.clear();
        this.waitingForCmdStart = false;
        this.pending = [];
        this.rejectPendingRequests(new Error('WebSocket gateway disconnected'));
        try {
            this.ws?.close();
        } catch {
            /* ignore */
        }
        this.connected = false;
    }

    async reconnect(): Promise<void> {
        // #576: as connect(), an ended session is not reopened.
        if (this.sessionEnded) throw new Error(SESSION_ENDED_MESSAGE);
        if (!this.connectionAllowed) throw new Error('Digital Twin MQTT requires a request from the parent');
        if (this.connectionPromise) return this.connectionPromise;
        this.closedByUser = false;
        this.tokenExpiredPending = false;
        if (this.ws?.readyState === WebSocket.OPEN && this.connected) {
            this.onConnected.next();
            return;
        }
        await this.connect();
    }
}

export const mqttGatewayClient = new MqttGatewayClient();

/**
 * #576: the window the sign-in replaces: the highest ancestor of this document on the same
 * origin (the IDE around its Digital Twin frame, the Host page around the IDE with ?sim=0),
 * or this window. null outside a browser.
 */
function signInWindow(): Window | null {
    if (typeof window === 'undefined') return null;
    let target: Window = window;
    try {
        while (target.parent && target.parent !== target) {
            void target.parent.location.href; // throws for an ancestor on another origin
            target = target.parent;
        }
    } catch {
        /* stop below the first ancestor on another origin */
    }
    return target;
}

/**
 * digitaltwins-service#576 (B3): the session-ended message: ended (the session is over;
 * the gateway does not know why, so the message does not guess: another sign-in, an idle
 * timeout and a sign-out elsewhere all look the same) or expired (the sign-in can no
 * longer be renewed). Plain DOM so it
 * works on every page that uses the gateway (IDE, Experience, RC) without each view
 * wiring it up. Shown once per page.
 */
function showSessionEndedNotice(kind: 'ended' | 'expired') {
    if (typeof document === 'undefined' || document.getElementById('ptv-session-ended')) return;
    const box = document.createElement('div');
    box.id = 'ptv-session-ended';
    box.setAttribute('role', 'alertdialog');
    box.style.cssText = 'position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,.55);font:15px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif';
    const card = document.createElement('div');
    card.style.cssText = 'max-width:440px;margin:16px;padding:22px 24px;border-radius:12px;background:#fff;color:#0f172a;box-shadow:0 12px 32px rgba(0,0,0,.25)';
    const title = document.createElement('div');
    title.style.cssText = 'font-weight:700;font-size:17px;margin-bottom:8px';
    title.textContent = kind === 'expired' ? 'Session expired' : 'Session ended';
    const body = document.createElement('div');
    body.style.cssText = 'margin-bottom:16px';
    body.textContent = kind === 'expired'
        ? 'Your sign-in has expired. Please sign in again to continue.'
        : 'Your session has ended. Please sign in again.';
    const button = document.createElement('a');
    const target = signInWindow();
    const here = target ? target.location : typeof location !== 'undefined' ? location : null;
    const returnUrl = here ? `${here.pathname}${here.search}${here.hash || ''}` : '/';
    const loginUrl = '/auth/login?returnUrl=' + encodeURIComponent(returnUrl || '/');
    button.href = loginUrl;
    if (target && target !== window) {
        // #576: in a frame, sign in in the page around it: the identity provider may not be framed.
        button.addEventListener('click', event => {
            event.preventDefault();
            target.location.assign(loginUrl);
        });
    }
    button.textContent = 'Sign in again';
    button.style.cssText = 'display:inline-block;padding:9px 16px;border-radius:8px;background:#2563eb;color:#fff;text-decoration:none;font-weight:600';
    card.append(title, body, button);
    box.append(card);
    (document.body || document.documentElement).appendChild(box);
}
